/**
 * INN I OG UT AV GJESTEMODUS (sak 232, T5).
 *
 * Inn: fanen navngir organisasjonen som delte i headeren (`auth/guest-org.ts`), `me` hentes på nytt
 * med den, og serveren må svare `guest` for den — ellers er det ingenting å åpne (delingen er
 * avsluttet), og fanen går tilbake. Så delingene, før noen side spør `can()`.
 *
 * Ut: headeren glemmes, `me` hentes uten den, og hele cachen friskes opp. Org-nøklene bærer
 * organisasjonen i seg, men de globale (`me`, konsertene) ble hentet med gjesteheaderen.
 */
import { guestScopeFromResponse } from '@tvitvi/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { qk } from '@/api/query-keys';
import { meQueryOptions } from '@/api/queries/me';
import {
  enterGuestOrganization,
  getGuestOrganizationId,
  leaveGuestOrganization,
  setGuestScope,
} from '@/auth/guest-org';

import { guestScopeQueryOptions } from './guest-queries';

export interface GuestMode {
  /** `true` når fanen står som gjest i `organizationId`; `false` når serveren ikke slapp den inn. */
  enter: (organizationId: string, from: string | null) => Promise<boolean>;
  leave: () => Promise<void>;
}

export function useGuestMode(): GuestMode {
  const queryClient = useQueryClient();

  /*
   * EN `me` SOM ALT ER UNDERVEIS, BLE SENDT MED DEN GAMLE HEADEREN (T8, målt 30. september).
   * `fetchQuery` gir den hentingen tilbake framfor å spørre på nytt, og da svarte serveren for
   * personens eget hus — «Åpne» ble til «Delingen er avsluttet». Den avbrytes først.
   */
  const refetchMe = useCallback(async () => {
    await queryClient.cancelQueries({ queryKey: qk.me() });
    return queryClient.fetchQuery({ ...meQueryOptions(), staleTime: 0 });
  }, [queryClient]);

  /*
   * GJESTEHUSETS SPØRRINGER HENTES IKKE IGJEN PÅ VEI UT (QA6 K5, L9). «Tilbake» går ut før den
   * navigerer, så sidene i gjestehuset står montert et øyeblikk til. `invalidateQueries` over alt
   * hentet dem da på nytt — med personens egen header — og `GET /v1/festivals` svarte 403. Nå
   * avbrytes det som er underveis, gjestehusets nøkler markeres gamle uten å hentes, og de hentes
   * med riktig header neste gang fanen går inn.
   */
  const leave = useCallback(async (): Promise<void> => {
    const guestOrg = getGuestOrganizationId();
    leaveGuestOrganization();
    await queryClient.cancelQueries();
    await refetchMe();
    if (guestOrg === null) {
      await queryClient.invalidateQueries({ queryKey: qk.all });
      return;
    }
    const guestKey = qk.org(guestOrg);
    const inGuestOrg = (key: readonly unknown[]): boolean =>
      guestKey.every((part, index) => key[index] === part);
    await queryClient.invalidateQueries({ queryKey: guestKey, refetchType: 'none' });
    await queryClient.invalidateQueries({
      queryKey: qk.all,
      predicate: (query) => !inGuestOrg(query.queryKey),
    });
  }, [queryClient, refetchMe]);

  const enter = useCallback(
    async (organizationId: string, from: string | null): Promise<boolean> => {
      enterGuestOrganization(organizationId, from);
      try {
        const me = await refetchMe();
        if (me.activeOrgRole !== 'guest' || me.activeOrganizationId !== organizationId) {
          await leave();
          return false;
        }
        const scope = await queryClient.fetchQuery({
          ...guestScopeQueryOptions(organizationId),
          staleTime: 0,
        });
        setGuestScope(organizationId, guestScopeFromResponse(scope));
        return true;
      } catch (error) {
        await leave();
        throw error;
      }
    },
    [leave, queryClient, refetchMe],
  );

  return { enter, leave };
}
